// src/components/study/ProtocolRedlineView.js
//
// Protocol Compare's Redline tab: every section of the new protocol (and the
// sections removed from the old one), with removed words struck through in red
// and added words underlined in green; a passage that moved without changing is
// shown once, in its new place, marked "Moved". The text and changes come from the
// server's redline.json (functions/protocolCompare.js buildRedline), found by
// code, not AI.
//
// Long protocols stay fast: changed sections only by default, 25 sections at a
// time, and long unchanged stretches folded ("… 340 unchanged words …").

import { useEffect, useMemo, useState } from "react";
import { FileText, ChevronDown, Download } from "lucide-react";

const FOLD_OVER = 60; // unchanged runs longer than this many words are folded
const KEEP = 20; // words kept at each end of a folded run
const PAGE = 25; // sections rendered per "Show more"

const STATUS_LABEL = { changed: "Changed", added: "Added", removed: "Removed", moved: "Moved", same: "No change" };

const sectionName = (s) => {
  const h = s.new || s.old || {};
  return `${h.number ? `${h.number} ` : ""}${h.title || ""}`.trim() || "Untitled section";
};
const pageText = (s) => {
  const o = s.old?.page;
  const n = s.new?.page;
  if (o != null && n != null) return o === n ? `p.${n}` : `old p.${o} → new p.${n}`;
  if (n != null) return `new p.${n}`;
  if (o != null) return `old p.${o}`;
  return "";
};

/** One unchanged run, folded when long. */
const EqText = ({ text }) => {
  const [open, setOpen] = useState(false);
  const words = text.split(" ");
  if (open || words.length <= FOLD_OVER) return <span>{text} </span>;
  return (
    <span>
      {words.slice(0, KEEP).join(" ")}{" "}
      <button type="button" onClick={() => setOpen(true)} className="protocol-compare-fold text-[11px] px-1.5 py-0.5 mx-0.5 rounded-md bg-white/[0.05] border border-white/10 text-slate-400 hover:text-slate-200">
        … {words.length - 2 * KEEP} unchanged words · show
      </button>{" "}
      {words.slice(-KEEP).join(" ")}{" "}
    </span>
  );
};

/** A section's text as segments; each change is anchored by its hunk id (for "Show in redline"). */
const SectionBody = ({ section }) => {
  const hunkAt = useMemo(() => new Map(section.hunks.map((h) => [h.segment, h])), [section]);
  const out = [];
  section.segments.forEach((g, i) => {
    const h = hunkAt.get(i);
    if (h) {
      out.push(
        <span key={`a${i}`} id={`pc-${h.id}`} className="protocol-compare-anchor">
          {(h.table || h.ocr) && (
            <span className="inline-flex gap-1 mr-1 align-middle">
              {h.table && <span className="protocol-compare-chip protocol-compare-chip--table text-[9px] px-1.5 py-0.5 rounded border font-semibold">Table · check both PDFs</span>}
              {h.ocr && <span className="protocol-compare-chip protocol-compare-chip--scanned text-[9px] px-1.5 py-0.5 rounded border font-semibold">Scanned · check the PDF</span>}
            </span>
          )}
        </span>
      );
    }
    if (g.t === "eq") out.push(<EqText key={i} text={g.x} />);
    else if (g.t === "mvin") {
      out.push(
        <span key={i} className="protocol-compare-moved rounded px-0.5 mr-1 bg-sky-500/10 text-sky-100">
          <span className="protocol-compare-chip protocol-compare-chip--moved text-[9px] px-1.5 py-0.5 mr-1 rounded border font-semibold align-middle">
            Moved here{g.from != null ? ` from old p.${g.from}` : ""}
          </span>
          <EqText text={g.x} />
        </span>
      );
    } else if (g.t === "mvout") {
      out.push(
        <span key={i} className="protocol-compare-chip protocol-compare-chip--moved text-[9px] px-1.5 py-0.5 mr-1 rounded border font-semibold align-middle">
          {g.n || ""} words moved{g.to != null ? ` to new p.${g.to}` : ""}
        </span>
      );
    } else if (g.t === "del") out.push(<del key={i} className="protocol-compare-del rounded px-0.5 mr-1 bg-rose-500/15 text-rose-200 line-through decoration-rose-300/80">{g.x}</del>);
    else out.push(<ins key={i} className="protocol-compare-ins rounded px-0.5 mr-1 bg-emerald-500/15 text-emerald-200 underline decoration-emerald-300/80">{g.x}</ins>);
  });
  if (!section.segments.length) out.push(<span key="empty" className="text-slate-500 italic">(heading only)</span>);
  return <p className="text-[13px] leading-relaxed text-slate-200 whitespace-pre-wrap break-words">{out}</p>;
};

/**
 * @param {{redline: {sections:Array, stats:object}, focusHunkId?: string|null,
 *          onOpenOld?: () => void, onOpenNew?: () => void,
 *          onDownload?: (onlyChanged: boolean) => void}} props
 */
const ProtocolRedlineView = ({ redline, focusHunkId = null, onOpenOld, onOpenNew, onDownload }) => {
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [shown, setShown] = useState(PAGE);
  const sections = useMemo(
    () => (redline?.sections || []).filter((s) => !onlyChanged || s.status !== "same"),
    [redline, onlyChanged]
  );

  // "Show in redline": make sure the section with that change is rendered, then scroll to it.
  useEffect(() => {
    if (!focusHunkId) return;
    const idx = sections.findIndex((s) => s.hunks.some((h) => h.id === focusHunkId));
    if (idx >= shown) setShown(idx + 1);
    const t = setTimeout(() => {
      const el = document.getElementById(`pc-${focusHunkId}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => clearTimeout(t);
  }, [focusHunkId, sections, shown]);

  if (!redline) return null;
  const st = redline.stats || {};
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input type="checkbox" checked={onlyChanged} onChange={(e) => { setOnlyChanged(e.target.checked); setShown(PAGE); }} />
          Only changed sections
        </label>
        <span className="text-[11px] text-slate-400">
          {st.sectionsChanged || 0} changed · {st.sectionsAdded || 0} added · {st.sectionsRemoved || 0} removed · {st.wordsAdded || 0} words added · {st.wordsRemoved || 0} removed
          {st.movedHunks ? ` · ${st.movedHunks} passage(s) moved` : ""}
        </span>
        <span className="flex-1" />
        {(onOpenOld || onOpenNew) && <span className="text-[10px] uppercase tracking-wider text-slate-500">Protocols</span>}
        {onOpenOld && (
          <button type="button" onClick={onOpenOld} className="protocol-compare-soft-btn inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10 text-[11px] font-semibold text-slate-200">
            <FileText className="w-3.5 h-3.5" /> Open old PDF
          </button>
        )}
        {onOpenNew && (
          <button type="button" onClick={onOpenNew} className="protocol-compare-soft-btn inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10 text-[11px] font-semibold text-slate-200">
            <FileText className="w-3.5 h-3.5" /> Open new PDF
          </button>
        )}
      </div>

      {onDownload && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 protocol-compare-panel rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <span className="text-[11px] text-slate-400">
            The redline is found by a text comparison, not AI. Check changes marked Table or Scanned against the PDFs.
          </span>
          <button type="button" onClick={() => onDownload(onlyChanged)} className="protocol-compare-solid-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-xs font-semibold text-white ring-1 ring-white/20">
            <Download className="w-3.5 h-3.5" /> Download redlined PDF
          </button>
        </div>
      )}

      {!sections.length && (
        <div className="protocol-compare-panel rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-slate-300">
          No differences were found between the two protocols.
        </div>
      )}

      {sections.length > 0 && (
        <nav aria-label="Changed sections" className="protocol-compare-panel rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-3 max-h-40 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Jump to a section</div>
          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
            {sections.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (i >= shown) setShown(i + 1);
                    setTimeout(() => document.getElementById(`pc-sec-${s.id}`)?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 50);
                  }}
                  className="text-left text-[12px] text-slate-300 hover:text-white truncate w-full"
                >
                  {sectionName(s)} <span className="text-slate-500">· {STATUS_LABEL[s.status]}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="space-y-3">
        {sections.slice(0, shown).map((s) => (
          <section key={s.id} id={`pc-sec-${s.id}`} className="protocol-compare-panel rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <header className="flex flex-wrap items-center gap-2 mb-2">
              {/* A heading added, removed or renamed is a change too; "Show in redline" lands here. */}
              {s.hunks.filter((h) => h.heading).map((h) => <span key={h.id} id={`pc-${h.id}`} className="protocol-compare-anchor" />)}
              <h3 className="text-sm font-semibold text-white">{sectionName(s)}</h3>
              <span className={`protocol-compare-chip protocol-compare-chip--${s.status} text-[10px] px-1.5 py-0.5 rounded border font-semibold`}>{STATUS_LABEL[s.status]}</span>
              {s.headingChanged && <span className="text-[11px] text-slate-400">heading was “{`${s.old.number ? `${s.old.number} ` : ""}${s.old.title}`}”</span>}
              {s.newHeading && <span className="text-[11px] text-slate-400">new heading (text below is unchanged)</span>}
              <span className="text-[11px] text-slate-500">{pageText(s)}</span>
            </header>
            <SectionBody section={s} />
          </section>
        ))}
      </div>

      {sections.length > shown && (
        <div className="mt-3 flex justify-center">
          <button type="button" onClick={() => setShown((n) => n + PAGE)} className="protocol-compare-soft-btn inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-xs font-semibold text-slate-200">
            <ChevronDown className="w-3.5 h-3.5" /> Show more sections ({sections.length - shown} left)
          </button>
        </div>
      )}
    </div>
  );
};

export default ProtocolRedlineView;
